import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, Cpu, Layers, Server, Timer } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { StatusDot } from "@/components/StatusDot";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { api, type OpsHealth } from "@/lib/api";
import { cn, fmtAgo, fmtTime } from "@/lib/utils";

const WORKER_TONE: Record<string, string> = {
  ok: "text-emerald-300", degraded: "text-amber-300",
  down: "text-red-300", gone: "text-zinc-500",
};

export default function Ops() {
  const { data, isLoading } = useQuery({
    queryKey: ["ops-health"],
    queryFn: () => api.get<OpsHealth>("/api/ops/health"),
    refetchInterval: 8_000,
  });
  const { data: nodes } = useQuery({
    queryKey: ["nodes"],
    queryFn: () => api.get<{ hostname: string; status: string; last_seen_at: string | null }[]>("/api/nodes"),
    refetchInterval: 15_000,
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}
        </div>
      </div>
    );
  }

  const q = data.queue;
  return (
    <div className="space-y-5">
      <PageHeader
        title={
          <span className="flex items-center gap-2.5">
            System
            <Badge variant={data.status === "ok" ? "success" : data.status === "degraded" ? "warning" : "destructive"}>
              {data.status}
            </Badge>
          </span>
        }
        description="Control-plane liveness: worker heartbeats, queue progress and edge nodes. A stalled loop degrades here even while the process still looks alive."
      />

      {data.reasons.length > 0 && (
        <Card className={cn("border", data.status === "down" ? "border-red-500/40 bg-red-500/5" : "border-amber-500/40 bg-amber-500/5")}>
          <CardContent className="flex items-start gap-3 px-5 py-4">
            <AlertTriangle className={cn("mt-0.5 h-4 w-4 shrink-0", data.status === "down" ? "text-red-300" : "text-amber-300")} />
            <ul className="space-y-1 text-sm">
              {data.reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="queued jobs" value={q.queued} icon={Layers}
          hint={`${q.runnable} runnable now`} />
        <StatCard label="leased (in-flight)" value={q.leased} icon={Activity} />
        <StatCard
          label="oldest runnable"
          value={q.oldest_runnable_age_s != null ? `${q.oldest_runnable_age_s}s` : "—"}
          icon={Timer}
          tone={q.oldest_runnable_age_s && q.oldest_runnable_age_s > 120 ? "text-amber-300" : undefined}
          hint="age of the longest-waiting queued job" />
        <StatCard
          label="failed (24h)" value={q.failed_24h} icon={AlertTriangle}
          tone={q.failed_24h > 0 ? "text-red-300" : "text-emerald-300"} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Cpu className="h-4 w-4" /> Workers
            </CardTitle>
            <CardDescription>
              Heartbeat every 15s — &gt;45s silent is degraded, &gt;120s down.
              Queue progress is checked too: a live process that completes
              nothing still degrades.
            </CardDescription>
          </CardHeader>
          <Table>
            <THead>
              <TR><TH>Worker</TH><TH>Status</TH><TH>Last beat</TH><TH>Completed</TH><TH>Depth seen</TH></TR>
            </THead>
            <TBody>
              {data.workers.map((w) => (
                <TR key={w.worker_id}>
                  <TD className="font-mono text-xs">{w.worker_id.slice(0, 12)}</TD>
                  <TD>
                    <span className={cn("flex items-center gap-2 text-xs", WORKER_TONE[w.status])}>
                      <StatusDot status={w.status} pulse={w.status !== "ok" && w.status !== "gone"} />
                      {w.status}
                    </span>
                  </TD>
                  <TD className="text-xs">{fmtAgo(w.last_seen_at)}</TD>
                  <TD className="num text-xs">{w.completed_total.toLocaleString()}</TD>
                  <TD className="num text-xs">{w.queue_depth}</TD>
                </TR>
              ))}
              {data.workers.length === 0 && (
                <TR><TD colSpan={5} className="py-6 text-center text-muted-foreground">
                  No worker has ever reported — is the worker container running?
                </TD></TR>
              )}
            </TBody>
          </Table>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Server className="h-4 w-4" /> Edge nodes
            </CardTitle>
            <CardDescription>BunkerWeb instances discovered by the scheduler</CardDescription>
          </CardHeader>
          <Table>
            <THead>
              <TR><TH>Hostname</TH><TH>Status</TH><TH>Last seen</TH></TR>
            </THead>
            <TBody>
              {(nodes ?? []).map((n) => (
                <TR key={n.hostname}>
                  <TD className="font-mono text-xs">{n.hostname}</TD>
                  <TD>
                    <span className="flex items-center gap-2 text-xs">
                      <StatusDot status={n.status} /> {n.status}
                    </span>
                  </TD>
                  <TD className="text-xs" title={fmtTime(n.last_seen_at)}>{fmtAgo(n.last_seen_at)}</TD>
                </TR>
              ))}
              {(nodes ?? []).length === 0 && (
                <TR><TD colSpan={3} className="py-6 text-center text-muted-foreground">
                  No instances registered yet.
                </TD></TR>
              )}
            </TBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
